import { useState, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";
import BannerUploader, { FocalPoint } from "@/components/base/BannerUploader";
import RichTextEditor from "@/components/base/RichTextEditor";
import { SUPPORT_PROGRAMS } from "@/pages/home/components/StatsSection";

type Lang = "vi" | "kr";
const T = (lang: Lang, vi: string, kr: string) => (lang === "vi" ? vi : kr);

interface SupportContent {
  slug: string;
  title_vi: string;
  title_kr: string;
  subtitle: string;
  content_html: string;
  icon: string;
  box_color: string;
  cover_image: string;
  display_order: number;
}

const DEFAULT_CONTENT: Record<string, Omit<SupportContent, "slug">> = {
  "ket-noi-doanh-nghiep": {
    title_vi: "Kết nối doanh nghiệp Hàn–Việt",
    title_kr: "비즈니스 매칭 지원",
    subtitle: "Tự động ghép cặp doanh nghiệp phù hợp theo ngành hàng và quy mô",
    content_html: "<h2>Dịch vụ Kết nối Doanh nghiệp</h2><p>Chúng tôi cung cấp dịch vụ kết nối doanh nghiệp Hàn Quốc và Việt Nam thông qua hệ thống ghép cặp thông minh.</p>",
    icon: "ri-exchange-2-line",
    box_color: "#004A99",
    cover_image: "",
    display_order: 1,
  },
  "dich-vu-phien-dich": {
    title_vi: "Dịch vụ phiên & biên dịch",
    title_kr: "통번역 서비스",
    subtitle: "Hỗ trợ dịch thuật tài liệu kỹ thuật và hợp đồng Hàn–Việt",
    content_html: "<h2>Dịch vụ Phiên & Biên dịch</h2><p>Đội ngũ phiên dịch viên chuyên nghiệp trong lĩnh vực kỹ thuật và thương mại.</p>",
    icon: "ri-translate-2",
    box_color: "#00AEEF",
    cover_image: "",
    display_order: 2,
  },
  "tu-van-thi-truong": {
    title_vi: "Tư vấn xâm nhập thị trường",
    title_kr: "시장 진출 컨설팅",
    subtitle: "Phân tích thị trường, chiến lược và hỗ trợ pháp lý xuất nhập khẩu",
    content_html: "<h2>Tư vấn Chiến lược Thị trường</h2><p>Dịch vụ tư vấn toàn diện giúp doanh nghiệp Hàn Quốc xâm nhập thị trường Việt Nam.</p>",
    icon: "ri-bar-chart-grouped-line",
    box_color: "#004A99",
    cover_image: "",
    display_order: 3,
  },
  "ho-tro-trien-lam": {
    title_vi: "Hỗ trợ tham gia triển lãm",
    title_kr: "전시회 참가 지원",
    subtitle: "Kết nối gian hàng tại các hội chợ thương mại Hàn Quốc và Việt Nam",
    content_html: "<h2>Hỗ trợ Tham gia Triển lãm</h2><p>Hỗ trợ doanh nghiệp tham gia các hội chợ và triển lãm thương mại lớn.</p>",
    icon: "ri-store-2-line",
    box_color: "#00AEEF",
    cover_image: "",
    display_order: 4,
  },
  "tim-kiem-doi-tac": {
    title_vi: "Tìm kiếm đối tác chiến lược",
    title_kr: "파트너 발굴 지원",
    subtitle: "Phân tích hồ sơ và giới thiệu đối tác phù hợp theo nhu cầu",
    content_html: "<h2>Tìm kiếm Đối tác Chiến lược</h2><p>Sử dụng công nghệ AI và mạng lưới doanh nghiệp để tìm kiếm đối tác phù hợp.</p>",
    icon: "ri-search-eye-line",
    box_color: "#004A99",
    cover_image: "",
    display_order: 5,
  },
};

const ICON_OPTIONS = [
  "ri-exchange-2-line", "ri-translate-2", "ri-bar-chart-grouped-line",
  "ri-store-2-line", "ri-search-eye-line", "ri-shake-hands-line",
  "ri-building-2-line", "ri-global-line", "ri-rocket-line",
  "ri-shield-check-line", "ri-customer-service-2-line", "ri-links-line",
];

interface Props { lang: Lang; }

const SupportProgramManager = ({ lang }: Props) => {
  const [activeSlug, setActiveSlug] = useState<string>(SUPPORT_PROGRAMS[0].slug);
  const [contents, setContents] = useState<Record<string, SupportContent>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [coverImages, setCoverImages] = useState<string[]>([]);
  const [coverFocal, setCoverFocal] = useState<FocalPoint>({ x: 50, y: 50 });
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [programOrder, setProgramOrder] = useState(SUPPORT_PROGRAMS.map((p) => p.slug));
  const dragRef = useRef<number | null>(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const { data } = await supabase.from("support_programs").select("*");
        const map: Record<string, SupportContent> = {};
        if (data) {
          (data as SupportContent[]).forEach((row) => { map[row.slug] = row; });
        }
        setContents(map);
      } catch { /* use defaults */ }
      setLoading(false);
    };
    fetchAll();
  }, []);

  const getContent = (slug: string): SupportContent => {
    return contents[slug] ?? { slug, ...DEFAULT_CONTENT[slug] };
  };

  const current = getContent(activeSlug);

  useEffect(() => {
    setCoverImages(current.cover_image ? [current.cover_image] : []);
  }, [activeSlug, current.cover_image]);

  const setField = (field: keyof SupportContent, val: unknown) => {
    setContents((prev) => ({
      ...prev,
      [activeSlug]: { ...getContent(activeSlug), [field]: val },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = { ...getContent(activeSlug), cover_image: coverImages[0] ?? "" };
    try {
      const { error } = await supabase
        .from("support_programs")
        .upsert(payload, { onConflict: "slug" });
      if (!error) {
        setContents((prev) => ({ ...prev, [activeSlug]: payload }));
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      }
    } catch { /* silent */ }
    setSaving(false);
  };

  // Drag-to-reorder
  const handleDragStart = (idx: number) => { setDraggingIdx(idx); dragRef.current = idx; };
  const handleDragOver = (e: React.DragEvent, idx: number) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop = (idx: number) => {
    if (dragRef.current === null || dragRef.current === idx) { setDraggingIdx(null); setDragOverIdx(null); return; }
    const newOrder = [...programOrder];
    const [moved] = newOrder.splice(dragRef.current, 1);
    newOrder.splice(idx, 0, moved);
    setProgramOrder(newOrder);
    setDraggingIdx(null);
    setDragOverIdx(null);
    dragRef.current = null;
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">{T(lang, "Quản lý Chương trình Hỗ trợ Hợp tác Hàn - Việt", "한-베 협력 지원 프로그램 관리")}</h1>
        <p className="text-sm text-gray-500 mt-1">{T(lang, "Chỉnh sửa nội dung, hình ảnh và sắp xếp thứ tự 5 mục chương trình hỗ trợ", "5개 지원 프로그램의 내용, 이미지 및 순서 관리")}</p>
      </div>

      <div className="flex gap-6">
        {/* Left: Program list (draggable) */}
        <div className="w-56 flex-shrink-0">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-3 px-1">{T(lang, "Kéo thả để sắp xếp", "드래그하여 순서 변경")}</p>
          <div className="space-y-1.5">
            {programOrder.map((slug, idx) => {
              const prog = SUPPORT_PROGRAMS.find((p) => p.slug === slug);
              if (!prog) return null;
              return (
                <div
                  key={slug}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={() => handleDrop(idx)}
                  onDragEnd={() => { setDraggingIdx(null); setDragOverIdx(null); }}
                  onClick={() => setActiveSlug(slug)}
                  className={`flex items-center gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                    activeSlug === slug
                      ? "border-[#004A99] bg-[#EDF2FB]"
                      : dragOverIdx === idx
                      ? "border-[#00AEEF]/50 bg-[#00AEEF]/5"
                      : "border-gray-200 bg-white hover:border-[#004A99]/30"
                  } ${draggingIdx === idx ? "opacity-40" : ""}`}
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-gray-400 cursor-grab">
                    <i className="ri-drag-move-2-line text-sm" />
                  </div>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${activeSlug === slug ? "bg-[#004A99]" : "bg-gray-100"}`}>
                    <i className={`${prog.icon} text-sm ${activeSlug === slug ? "text-white" : "text-gray-500"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold truncate ${activeSlug === slug ? "text-[#004A99]" : "text-gray-700"}`}>{prog.titleVi}</p>
                    <p className="text-[10px] text-gray-400 truncate">{prog.titleKr}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Editor */}
        <div className="flex-1 min-w-0 bg-white rounded-2xl border border-gray-200 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${current.box_color}20` }}>
                  <i className={`${current.icon} text-xl`} style={{ color: current.box_color }} />
                </div>
                <div>
                  <h2 className="font-bold text-gray-800 text-sm">{current.title_vi}</h2>
                  <p className="text-[11px] text-gray-400">{current.title_kr}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{T(lang, "Tiêu đề tiếng Việt", "베트남어 제목")}</label>
                  <input className={inputCls} value={current.title_vi} onChange={(e) => setField("title_vi", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{T(lang, "Tiêu đề tiếng Hàn", "한국어 제목")}</label>
                  <input className={inputCls} value={current.title_kr} onChange={(e) => setField("title_kr", e.target.value)} />
                </div>
              </div>

              <div>
                <label className={labelCls}>{T(lang, "Mô tả ngắn (Subtitle)", "짧은 설명")}</label>
                <input className={inputCls} value={current.subtitle} onChange={(e) => setField("subtitle", e.target.value)} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Icon picker */}
                <div>
                  <label className={labelCls}>{T(lang, "Icon", "아이콘")}</label>
                  <div className="grid grid-cols-6 gap-1.5 p-3 border border-gray-200 rounded-lg bg-[#F8F9FA]">
                    {ICON_OPTIONS.map((ic) => (
                      <button
                        key={ic}
                        type="button"
                        onClick={() => setField("icon", ic)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
                          current.icon === ic ? "bg-[#004A99] text-white" : "bg-white text-gray-500 hover:bg-[#004A99]/10"
                        }`}
                      >
                        <i className={`${ic} text-sm`} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Box color */}
                <div>
                  <label className={labelCls}>{T(lang, "Màu hộp (Box Color)", "박스 색상")}</label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={current.box_color}
                        onChange={(e) => setField("box_color", e.target.value)}
                        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                      />
                      <input
                        className={`${inputCls} flex-1`}
                        value={current.box_color}
                        onChange={(e) => setField("box_color", e.target.value)}
                        placeholder="#004A99"
                      />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {["#004A99", "#00AEEF", "#003d7d", "#001428", "#00796B", "#E65100"].map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setField("box_color", c)}
                          className={`w-7 h-7 rounded-lg border-2 cursor-pointer transition-all ${current.box_color === c ? "border-gray-800 scale-110" : "border-transparent"}`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                    {/* Preview */}
                    <div className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: `${current.box_color}15` }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${current.box_color}50` }}>
                        <i className={`${current.icon} text-sm`} style={{ color: current.box_color }} />
                      </div>
                      <span className="text-xs font-semibold" style={{ color: current.box_color }}>{current.title_vi}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cover image */}
              <BannerUploader
                imageUrl={coverImages[0] ?? ""}
                focalPoint={coverFocal}
                onChange={(url, focal) => {
                  setCoverImages(url ? [url] : []);
                  setCoverFocal(focal);
                  setField("cover_image", url);
                }}
                aspectRatio={21 / 9}
                folder="support-programs"
                label={T(lang, "Ảnh bìa trang chi tiết (21:9 · Khuyến nghị >1920px)", "상세 페이지 커버 이미지 (21:9 · 1920px 이상 권장)")}
                previewLabel={T(lang, "Xem trước", "미리보기")}
              />

              {/* Rich text editor */}
              <RichTextEditor
                value={current.content_html}
                onChange={(html) => setField("content_html", html)}
                placeholder={T(lang, "Nhập nội dung chi tiết cho trang này...", "이 페이지의 상세 내용을 입력하세요...")}
                minHeight={300}
                folder="support-programs"
                label={T(lang, "Nội dung trang chi tiết (Rich Text)", "상세 페이지 내용 (리치 텍스트)")}
              />

              {/* Save */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <a
                  href={`/support/${activeSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-[#004A99] font-medium cursor-pointer"
                >
                  <i className="ri-external-link-line" />
                  {T(lang, "Xem trang chi tiết", "상세 페이지 보기")}
                </a>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                    saved ? "bg-green-600 text-white" : "bg-[#004A99] hover:bg-[#003a7a] text-white"
                  } disabled:opacity-50`}
                >
                  {saving ? <i className="ri-loader-4-line animate-spin" /> : saved ? <i className="ri-check-line" /> : <i className="ri-save-line" />}
                  {saving ? T(lang, "Đang lưu...", "저장 중...") : saved ? T(lang, "Đã lưu!", "저장됨!") : T(lang, "Lưu thay đổi", "변경 사항 저장")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportProgramManager;
